import type { FormEventHandler } from 'react'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import TextInput from 'components/Common/Form/Input/TextInput'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { formToObject } from 'lib/forms/formToObject'
import TextButton from 'LuxKit/Button/TextButton'
import ReCAPTCHA from 'react-google-recaptcha'
import config from 'constants/config'
import BodyText from 'LuxKit/Typography/BodyText'
import LogoIcon from 'components/App/Header/Logo/LogoIcon'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import LegalText from 'components/Account/AccountAccess/AccountAccessLegalText'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import noop from 'lib/function/noop'
import { checkUserExists } from 'actions/AuthActions'
import { checkAgentHubStatus } from 'actions/AgentHubActions'
import Caption from 'LuxKit/Typography/Caption'
import ModalContext from 'contexts/ModalContext'
import AgentHubExpressionOfInterestModal from '../AgentHubExpressionOfInterestModal'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import TextLink from 'LuxKit/TextLink'
import { pushWithRegion } from 'actions/NavigationActions'
import { LE_AGENT_HUB_COMPLETE_ACCOUNT_SETUP } from 'agentHub/routes/AgentHubRoutes'
import qs from 'qs'
import { agentHubOrSubBrandConfig } from 'agentHub/constants'

interface LoginFormData {
  email: string
}

interface Props {
  onModeChange?: (mode: App.UiAccountModalMode) => void
  onSubmit?: (userId: string) => void
  defaultUser?: App.User
  dismissable?: boolean
}

function AgentHubLoginForm(props: Props) {
  const {
    onModeChange = noop,
    onSubmit = noop,
    defaultUser,
    dismissable,
  } = props

  const intl = useIntl()
  const appDispatch = useAppDispatch()
  const showModal = useContext(ModalContext)

  const [userEmail, setUserEmail] = useState<string>('')
  const [isError, setIsError] = useState(false)
  const authProcessing = useAppSelector(state => state.auth.processing)
  const user = useAppSelector(state => state.auth.users[userEmail])
  const agentStatus = useAppSelector(state => state.agentHub.agentStatus[userEmail])

  const authError = useAppSelector(state => state.auth.error)

  const formRef = useRef<HTMLFormElement>(null)
  const emailInputRef = useRef<HTMLInputElement>(null)
  const reRef = useRef<ReCAPTCHA>(null)

  const modalContext = useModalElementContext()

  const showRegisterModal = useCallback(() => {
    modalContext.resolve()
    showModal(<AgentHubExpressionOfInterestModal />)
  }, [modalContext, showModal])

  const handleSubmission = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()
    const formData = formToObject<LoginFormData>(event.currentTarget)
    setUserEmail(formData.email)
    onSubmit(formData.email)
    appDispatch(checkUserExists({ email: formData.email }, reRef.current ?? undefined))
    appDispatch(checkAgentHubStatus(formData.email))

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function onChange() {
    setIsError(false)
    emailInputRef.current?.setCustomValidity('')
  }

  useEffect(() => {
    formRef.current?.reset()
    emailInputRef.current?.focus()
  }, [])

  useEffect(() => {
    if (userEmail && !authProcessing && agentStatus && user) {
      if (agentStatus.data?.action === 'onboard') {
        if (user.exists) {
          const query = qs.stringify({ token: agentStatus.data.agentId })
          modalContext.resolve()
          appDispatch(pushWithRegion(LE_AGENT_HUB_COMPLETE_ACCOUNT_SETUP, query))
        } else {
          onModeChange('agentResendActivation')
        }
        return
      }

      if (user.exists && agentStatus.data?.action === 'login') {
        onModeChange('loginPassword')
        setUserEmail('')
        return
      }

      setIsError(true)
      setUserEmail('')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail, authProcessing, agentStatus, modalContext, user])

  return <>
    <ModalHeader title="" dismissible={dismissable} />
    <ModalBody>
      <ModalContent>
        <form
          ref={formRef}
          name="agentHubLoginForm"
          data-testid="agent-hub-login-form"
          onSubmit={handleSubmission}
        >
          <Group direction="vertical" gap={12}>
            <Group direction="vertical" gap={24}>
              {authError?.message && <MessageBanner kind="critical">{authError.message}</MessageBanner>}

              <Group direction="horizontal" horizontalAlign="center"><LogoIcon /></Group>

              <Group direction="vertical" gap={4}>
                <Heading variant="heading4" align="center">
                  {intl.formatMessage({
                    id: 'E_qc34FoP0_RMj_dkW1N',
                    defaultMessage: 'Earn commission on the world’s best travel deals through {displayShortName}',
                    description: 'Agent Hub login form title promoting agent benefits',
                  }, {
                    displayShortName: agentHubOrSubBrandConfig.displayShortName,
                  })}
                </Heading>
                <BodyText variant="medium" align="center">
                  {intl.formatMessage({
                    id: 'P2dCMvqky-_LHHbEYbYA',
                    defaultMessage: 'Savings for your clients, commission in your pocket',
                    description: 'Agent Hub login form subtitle promoting agent benefits',
                  })}
                </BodyText>
              </Group>

              <Group direction="vertical" gap={12}>
                <TextInput
                  label={intl.formatMessage({
                    id: 'xd8kCUKuJNwenrB0AK4V',
                    defaultMessage: 'Email address',
                    description: 'Agent Hub login email input label in the travel agent hub portal',
                  })}
                  required
                  type="email"
                  name="email"
                  autoComplete="email"
                  placeholder={intl.formatMessage({
                    id: 'demJ_P8GYNhsDCDtWsql',
                    defaultMessage: 'Email',
                    description: 'Agent Hub login email input placeholder in the travel agent hub portal',
                  })}
                  requiredErrorMessage={intl.formatMessage({
                    id: 'ZsvROTxPDbGcASad07L8',
                    defaultMessage: 'Email is required',
                    description: 'Agent Hub login email required validation error in the agent hub portal',
                  })}
                  ref={emailInputRef}
                  onChange={onChange}
                  defaultValue={defaultUser?.email}
                />
                {isError &&
                  <Caption variant="medium" colour="critical">
                    <FormattedMessage
                      id="6uw8jlsiFgw_RjqlAl5y"
                      defaultMessage="This {displayShortName} account does not exist. Try again or <link>Register Now</link>"
                      description="Agent Hub login error message with register link in the agent hub portal"
                      values={{
                        displayShortName: agentHubOrSubBrandConfig.displayShortName,
                        link: (chunks) => (
                          <TextLink size="caption" onClick={showRegisterModal} weight="regular">
                            {chunks}
                          </TextLink>
                        ),
                      }}
                    />
                  </Caption>}
                <TextButton
                  kind="primary"
                  size="large"
                  data-testid="to-login-continue"
                  type="submit"
                  fit="flex"
                >
                  {intl.formatMessage({
                    id: '3tuZGn3h9IGer7Cyh-L6',
                    defaultMessage: 'Continue',
                    description: 'Agent Hub login form continue button in the travel agent hub portal',
                  })}
                </TextButton>
              </Group>
            </Group>
            <LegalText />
          </Group>
          {config.RECAPTCHA_KEY && <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={reRef} />}
        </form>
      </ModalContent>
    </ModalBody>
  </>
}

export default AgentHubLoginForm
